import React, { useState, useEffect } from "react";
import SalesDrawer, { SalesClient } from "./SalesDrawer";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../api/url";
import { getSalesDashboard } from "../../services/sales";

// ─── Random-but-stable avatar colours ────────────────────────────────────────
const PALETTE = [
  "#7C3AED", "#3B82F6", "#F59E0B", "#10B981", "#EF4444",
  "#8B5CF6", "#06B6D4", "#EC4899", "#F97316", "#00A9A9",
];
const avatarColor = (seed: string) =>
  PALETTE[
    seed.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) & 0xffff, 0) % PALETTE.length
  ];

// ─── Column config ────────────────────────────────────────────────────────────
const COLUMNS: { key: SalesClient["currentStage"]; label: string; barColor: string }[] = [
  { key: "new_user",             label: "New user",            barColor: "#00A9A9" },
  { key: "call_request",        label: "Call request",        barColor: "#7C3AED" },
  { key: "assign_nutritionist", label: "Assign nutritionist", barColor: "#F59E0B" },
  { key: "schedule_meeting",    label: "Schedule meeting",    barColor: "#3B82F6" },
  { key: "share_meal",          label: "Share meal",          barColor: "#10B981" },
];

// ─── API response types ───────────────────────────────────────────────────────
interface ApiUser {
  user_id: number;
  name: string;
  profile_image: string;
  goal: string;
  account_created: string;
  activity_status: "Active" | "Idle";
  call_requested_at?: string;
  call_completed_at?: string;
  call_status?: string;
  notes?: string;
  plan_purchased_at?: string;
  call_scheduled_at?: string;
  meeting_link?: string;
  nutritionist?: { id: number; name: string };
}

interface ApiDashboard {
  new_users:            { count: number; users: ApiUser[] };
  call_requests:        { count: number; users: ApiUser[] };
  assign_nutritionist:  { count: number; users: ApiUser[] };
  schedule_meeting:     { count: number; users: ApiUser[] };
  share_meal:           { count: number; users: ApiUser[] };
}

interface ApiResponse {
  success: string;
  message: string;
  dashboard: ApiDashboard;
}

// ─── Extra card metadata ──────────────────────────────────────────────────────
interface CardMeta {
  clientId: string;
  stage: SalesClient["currentStage"];
  status?: "Active" | "Idle";
  dateTime?: string;
  callDone?: boolean;
  goal?: string;
  nutritionist?: string;
}

// ─── Map API response → clients + meta ───────────────────────────────────────
function mapApiToClientsAndMeta(dashboard: ApiDashboard): {
  clients: SalesClient[];
  meta: CardMeta[];
} {
  const clients: SalesClient[] = [];
  const meta: CardMeta[] = [];

  const baseClient = (user: ApiUser): SalesClient => ({
    id: String(user.user_id),
    name: user.name || "Unknown",
    initials: (user.name || "?")[0]?.toUpperCase() ?? "?",
    avatarColor: avatarColor(user.name || String(user.user_id)),
    avatarImage: user.profile_image ? `${BASE_URL}${user.profile_image}` : undefined,
    currentStage: "new_user",
    joinedDate: user.account_created,
    goal: user.goal || undefined,
  });

  // Users who appear in advanced sections must not also appear in new_user column.
  // new_users contains ALL users; we only place them in new_user if they are not
  // already placed in a more specific stage.
  const advancedUserIds = new Set([
    ...dashboard.call_requests.users.map((u) => u.user_id),
    ...dashboard.assign_nutritionist.users.map((u) => u.user_id),
    ...dashboard.schedule_meeting.users.map((u) => u.user_id),
    ...dashboard.share_meal.users.map((u) => u.user_id),
  ]);

  for (const user of dashboard.new_users.users) {
    if (advancedUserIds.has(user.user_id)) continue;
    clients.push({ ...baseClient(user), currentStage: "new_user" });
    meta.push({ clientId: String(user.user_id), stage: "new_user", status: user.activity_status });
  }

  for (const user of dashboard.call_requests.users) {
    const isCompleted = user.call_status === "Completed";
    clients.push({ ...baseClient(user), currentStage: "call_request", hasCallRequest: true, callRequestedAt: user.call_requested_at, callCompleted: isCompleted, callCompletedAt: user.call_completed_at });
    meta.push({
      clientId: String(user.user_id),
      stage: "call_request",
      dateTime: isCompleted && user.call_completed_at ? user.call_completed_at : user.call_requested_at,
      callDone: isCompleted,
    });
  }

  for (const user of dashboard.assign_nutritionist.users) {
    clients.push({ ...baseClient(user), currentStage: "assign_nutritionist" });
    meta.push({ clientId: String(user.user_id), stage: "assign_nutritionist", goal: user.goal || "No data" });
  }

  for (const user of dashboard.schedule_meeting.users) {
    clients.push({
      ...baseClient(user),
      currentStage: "schedule_meeting",
      assignedNutritionist: user.nutritionist?.name,
    });
    meta.push({ clientId: String(user.user_id), stage: "schedule_meeting", nutritionist: user.nutritionist?.name });
  }

  for (const user of dashboard.share_meal.users) {
    clients.push({ ...baseClient(user), currentStage: "share_meal", assignedNutritionist: user.nutritionist?.name });
    meta.push({ clientId: String(user.user_id), stage: "share_meal", nutritionist: user.nutritionist?.name });
  }

  return { clients, meta };
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar: React.FC<{ client: SalesClient; size?: number; grayout?: boolean }> = ({ client, size = 32, grayout = false }) => {
  const [imgError, setImgError] = useState(false);

  if (client.avatarImage && !imgError) {
    return (
      <img src={client.avatarImage} alt={client.name}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size, filter: grayout ? "grayscale(100%)" : undefined }}
        onError={() => setImgError(true)} />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: grayout ? "#D1D5DB" : client.avatarColor }}
    >
      <span className="font-semibold font-inter" style={{ fontSize: size * 0.4, color: grayout ? "#9CA3AF" : "#ffffff" }}>
        {client.initials}
      </span>
    </div>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
const SalesCard: React.FC<{
  client: SalesClient;
  meta: CardMeta;
  barColor: string;
  onClick: () => void;
}> = ({ client, meta, barColor, onClick }) => {
  const isDone = meta.callDone === true;
  return (
  <button
    onClick={onClick}
    className="w-full text-left bg-white rounded-md border border-[#EFEFEF] overflow-hidden
               hover:shadow-sm transition-shadow duration-200 cursor-pointer"
  >
    {/* Top row: bar + avatar + name */}
    <div className="flex items-center gap-2.5 px-3 py-3 min-w-0">
      {/* Coloured vertical bar — inside card, not at edge */}
      <div
        className="flex-shrink-0 rounded-full self-stretch"
        style={{ width: "3px", backgroundColor: isDone ? "#D1D5DB" : barColor, minHeight: "28px" }}
      />
      <Avatar client={client} size={32} grayout={isDone} />
      <span
        className="text-[11px] md:text-[12px] xl:text-[13px] 3xl:text-sm font-medium font-inter leading-tight truncate"
        style={{ color: isDone ? "#9CA3AF" : "#1E1F26" }}
      >
        {client.name}
      </span>
    </div>

    {/* Horizontal separator */}
    <div className="h-px bg-[#F0F0F0] mx-3" />

    {/* Meta row */}
    <div className="px-3 py-2.5 min-w-0">
      {meta.status && (
        <p className="text-[10px] md:text-[11px] xl:text-[12px] 3xl:text-xs font-inter font-medium truncate"
          style={{ color: meta.status === "Active" ? "#00A9A9" : "#F59E0B" }}>
          {meta.status}
        </p>
      )}
      {meta.dateTime && (
        <div className="flex items-center gap-1.5 min-w-0">
          <p
            className="text-[10px] md:text-[11px] xl:text-[11px] 3xl:text-xs font-inter font-medium truncate"
            style={{ color: isDone ? "#9CA3AF" : "#000000" }}
          >{meta.dateTime}</p>
          {isDone && (
            <span className="flex-shrink-0 text-[9px] font-semibold font-inter text-[#00A9A9] bg-[#C8EFEF] px-1.5 py-0.5 rounded-full leading-none">
              Done
            </span>
          )}
        </div>
      )}
      {meta.goal && (
        <p className="text-[10px] md:text-[11px] xl:text-[11px] 3xl:text-xs font-inter font-medium truncate"
          style={{ color: "#000000" }}>
          {meta.goal}
        </p>
      )}
      {meta.nutritionist && (
        <p className="text-[10px] md:text-[11px] xl:text-[11px] 3xl:text-xs font-inter font-medium text-[#000000] truncate">{meta.nutritionist}</p>
      )}
    </div>
  </button>
  );
};

const STAGE_ORDER = [
  "new_user", "call_request", "assign_nutritionist", "schedule_meeting", "share_meal",
] as const;

// ─── SalesPage ────────────────────────────────────────────────────────────────
const SalesPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<SalesClient[]>([]);
  const [cardMeta, setCardMeta] = useState<CardMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<SalesClient | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const data: ApiResponse = await getSalesDashboard();
        if (data.success === "1" && data.dashboard) {
          const { clients: fetched, meta } = mapApiToClientsAndMeta(data.dashboard);
          setClients(fetched);
          setCardMeta(meta);
        }
      } catch (err) {
        console.error("Failed to fetch sales dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handleCardClick = (client: SalesClient) => {
    const latest = clients.find((c) => c.id === client.id) ?? client;
    setSelectedClient(latest);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedClient(null), 300);
  };

  const handleAssignNutritionist = (clientId: string) => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedClient(null), 300);
    navigate(`/sales-clients/${clientId}`);
  };

  // Advance client to the next stage and record the completed step date.
  // Special case: confirming "call_request" keeps the client in the call_request column
  // (callCompleted = true). The client only moves to the next column for all other steps.
  const handleStageAdvance = (clientId: string, stepKey: string, completedAt: string) => {
    setClients((prev) =>
      prev.map((c) => {
        if (c.id !== clientId) return c;

        let updated: SalesClient;

        if (stepKey === "call_request") {
          updated = {
            ...c,
            callCompleted: true,
            completedSteps: { ...(c.completedSteps ?? {}), [stepKey]: completedAt },
          };
        } else {
          const currentIdx = STAGE_ORDER.indexOf(c.currentStage as typeof STAGE_ORDER[number]);
          const nextStage = STAGE_ORDER[Math.min(currentIdx + 1, STAGE_ORDER.length - 1)];
          updated = {
            ...c,
            currentStage: nextStage,
            completedSteps: { ...(c.completedSteps ?? {}), [stepKey]: completedAt },
          };
        }

        setSelectedClient((prev) => (prev?.id === clientId ? updated : prev));
        return updated;
      })
    );

    if (stepKey === "call_request") {
      setCardMeta((prev) =>
        prev.map((m) =>
          m.clientId === clientId && m.stage === "call_request"
            ? { ...m, callDone: true }
            : m
        )
      );
    }
  };

  const getMeta = (id: string, stage: SalesClient["currentStage"]): CardMeta =>
    cardMeta.find((m) => m.clientId === id && m.stage === stage) ?? { clientId: id, stage };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-lg md:text-xl xl:text-[22px] 3xl:text-2xl font-bold font-inter text-[#1E1F26]">Sales</h1>
      </div>

      {/* Kanban board — 5 equal columns */}
      <div className="px-5 pb-5">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-sm font-inter text-[#9CA3AF]">Loading...</p>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
            {COLUMNS.map((col) => {
              const colClients = clients.filter((c) => c.currentStage === col.key);
              return (
                <div
                  key={col.key}
                  className="flex flex-col bg-white rounded-md border border-[#EFEFEF] min-w-0"
                >
                  {/* Column header */}
                  <div className="px-4 py-3 flex items-center justify-between gap-1 min-w-0">
                    <span className="text-[11px] md:text-[12px] xl:text-[13px] 3xl:text-sm font-semibold font-inter text-[#1E1F26] truncate">
                      {col.label}
                    </span>
                    <span className="text-[10px] md:text-[11px] xl:text-[12px] 3xl:text-xs font-inter text-[#1F1F1F] font-medium bg-[#EFEFEF] px-2 py-0.5 rounded-full">
                      {colClients.length}
                    </span>
                  </div>

                  {/* Cards — done cards sorted to bottom */}
                  <div className="px-3 pb-3 flex flex-col gap-2">
                    {[...colClients]
                      .sort((a, b) => {
                        const aDone = getMeta(a.id, col.key).callDone ? 1 : 0;
                        const bDone = getMeta(b.id, col.key).callDone ? 1 : 0;
                        return aDone - bDone;
                      })
                      .map((client) => (
                        <SalesCard
                          key={client.id}
                          client={client}
                          meta={getMeta(client.id, col.key)}
                          barColor={col.barColor}
                          onClick={() => handleCardClick(client)}
                        />
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Drawer */}
      <SalesDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        client={selectedClient}
        onOpenChat={(client) => { setDrawerOpen(false); navigate("/chat", { state: { openChatForUserId: client.id } }); }}
        onStageAdvance={handleStageAdvance}
        onAssignNutritionist={handleAssignNutritionist}
      />
    </div>
  );
};

export default SalesPage;
